"use client";

// WO-GF-001-S20 · F131 → S21 — SHARED FUNNEL CHROME (F122 site bar + stepper + re-query search bar).
//
// Built to BOOKING-FLOW-CONTRACT-GF-001.md + the S21 founder re-walk (F132/F138/F139). F131's presentation
// was rejected a second time; this pass fixes the dead search bar (F132), adds the missing site chrome +
// mountain pill (F139), and tightens the design tokens to live's measured values (F138).
//
// PH2-RESKIN, declared: live's funnel pages sit behind Search and could never be crawled, so there is no
// captured CSSOM for them and no byte-for-byte pixel baseline is claimed. Structure + ORDER + tokens follow
// live (Roboto; $orangeColor #f15a22, $primaryColor #828282, $secondaryOpacity #cbe0e4). The reusable site
// helpers (hudNavHref / resortHubHref / RESORTS) are the SAME ones the mirror hub header uses, so the
// funnel bar's mode links and change-mountain menu can never drift from the rest of the site.

import { useState } from "react";
import { useCartStore } from "@/lib/cart-store";
import { hudNavHref } from "@/components/site/hub-nav";
import { mountainBadgeSrc } from "@/components/site/mountain-context";
import { openMountainSelect } from "@/components/site/MountainSelectLayer";
import { MemberAvatarImg } from "@/components/site/MemberAvatar";

// ── WO-COMMERCE-BIND-001 · DoD 10 (§13) — THE FUNNEL NOW WEARS THE FOUNDER'S DESIGN ──────────────
//
// Founder, on the DoD 3a evidence: "review and pay looks like a different non-new front end." He was
// right, and this is the mechanism: the three constants below were LIVE's tokens ($orangeColor
// #f15a22 / $primaryColor #828282 / $secondaryOpacity #cbe0e4, Roboto), so the purchase path was the
// one place a customer crossed OUT of the new design and into a mirror of the old site, mid-flow.
//
// §13 ordered a STRUCTURAL search of the export before choosing restyle-vs-port, and it changed the
// answer. The export DOES carry this checkout — `page:'pay'` with steps method/card/review/done, its
// own funnel rail (Search · Depart · Return · Details · Booking store · Review & pay), and the
// headings HOW ARE YOU PAYING? / CARD DETAILS / REVIEW &amp; PAY. §13's "0 hits in both copies" was an
// HTML-ENTITY artifact: the export writes `REVIEW &amp; PAY`, which a raw-ampersand grep cannot see.
//
// What is NOT done, and why (measured, not preference): the export's own pay flow is NOT ported as
// the purchase path. Its `payNext` mints `"SB-" + Math.random()` at the review step and paints a
// booking confirmation with no server call — the fabricated-financial-record class DoD 5 removed —
// and it carries none of the live departures, live per-operator terms or the §14 legacy submit these
// screens are wired to. So: the founder's DESIGN, this build's DATA and rails. Every value below is
// quoted from the export's own checkout markup; nothing here is a new design language.
// moved to @/lib/booking-chrome-tokens (boundary-free); re-exported so client imports are unchanged
export { ORANGE, GREY } from "@/lib/booking-chrome-tokens";
import { ORANGE, GREY } from "@/lib/booking-chrome-tokens";
export const RULE = "var(--sg-border)"; //    #E4E9EC — the export's hairline

/**
 * The export's checkout visual system, lifted from its own markup so the funnel cannot drift from it.
 * Byte offsets are into `apps/root/public/design/sg-beta-v2.01.html` (the served copy) at the commit
 * this was read: the pay screens 460k-475k, their vals 1025k-1032k, the funnel overlay 484k-489k.
 *
 * `--sg-navy` is the ONE token the export defines internally and `@repo/tokens/design-system.css`
 * does not, so it is written as its literal. It is deliberately NOT added to the token file: that
 * file's header says it is a verbatim mirror of the founder's design system and "every edit made
 * here is drift that the next sync silently reverts".
 */
export const NAVY = "#133B55";
export const FUNNEL = {
  /** page shell — export: `max-width: 1140px`, `padding: 26px clamp(14px, 3vw, 24px)`, on #fff */
  page: { maxWidth: 1140, margin: "0 auto", padding: "26px clamp(14px, 3vw, 24px) 72px" } as React.CSSProperties,
  /** screen title — export: font-display, clamp(26px, 7vw, 34px), weight 400, uppercase */
  h1: {
    margin: "0 0 4px",
    fontFamily: "var(--sg-font-display)",
    fontSize: "clamp(26px, 7vw, 34px)",
    fontWeight: 400,
    textTransform: "uppercase",
    color: "var(--sg-ink-900)",
  } as React.CSSProperties,
  /** the line under every export screen title */
  lede: { margin: "0 0 20px", fontSize: 13.5, color: "var(--sg-grey-600)" } as React.CSSProperties,
  /** export panel: #fff, 1px var(--sg-border), radius 12, padding 18 */
  panel: { background: "#fff", border: `1px solid ${RULE}`, borderRadius: 12, padding: 18 } as React.CSSProperties,
  /** export panel heading: font-secondary 11px/700, .1em, uppercase */
  panelHead: {
    margin: "0 0 14px",
    fontFamily: "var(--sg-font-secondary)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "var(--sg-ink-900)",
  } as React.CSSProperties,
  /** export field label: font-secondary 10px/700, .1em, uppercase, grey-500 */
  label: {
    display: "block",
    marginBottom: 5,
    fontFamily: "var(--sg-font-secondary)",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "var(--sg-grey-500)",
  } as React.CSSProperties,
  /** export input: 44px, 1px var(--sg-glacier-200), radius 10, 14.5px */
  input: {
    width: "100%",
    height: 44,
    padding: "0 13px",
    background: "#fff",
    border: "1px solid var(--sg-glacier-200)",
    borderRadius: 10,
    fontFamily: "var(--sg-font-web)",
    fontSize: 14.5,
    color: "var(--sg-ink-900)",
    boxSizing: "border-box",
  } as React.CSSProperties,
  /** export primary action: 50px, radius 12, orange, font-secondary 13/700 uppercase .09em */
  cta: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 50,
    padding: "0 30px",
    background: ORANGE,
    color: "#fff",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    fontFamily: "var(--sg-font-secondary)",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    textDecoration: "none",
  } as React.CSSProperties,
  /** export secondary action: 48px, #fff, 1px var(--sg-border-strong), radius 10 */
  ctaGhost: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    padding: "0 22px",
    background: "#fff",
    color: "var(--sg-ink-900)",
    border: "1px solid var(--sg-border-strong)",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "var(--sg-font-secondary)",
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    textDecoration: "none",
  } as React.CSSProperties,
  /** the export's summary rail (`sideW` 340px desktop / full width under 900px) */
  rail: {
    flex: "none",
    width: 340,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    padding: "20px 22px",
    border: "1px solid var(--sg-glacier-200)",
    borderRadius: 14,
    background: "#fff",
  } as React.CSSProperties,
  /** the export's line-item row inside REVIEW & PAY */
  line: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 17px",
    border: `1px solid ${RULE}`,
    borderRadius: 12,
  } as React.CSSProperties,
  /** the export's micro-chip (operator / class), font-secondary 9-11px uppercase */
  chip: {
    display: "inline-block",
    padding: "2px 9px",
    border: "1px solid var(--sg-glacier-200)",
    borderRadius: 999,
    fontFamily: "var(--sg-font-secondary)",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: "var(--sg-glacier-700)",
  } as React.CSSProperties,
  /** the export's total figure: font-primary 24/800, -0.03em, navy */
  total: {
    fontFamily: "var(--sg-font-primary)",
    fontSize: 24,
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: NAVY,
  } as React.CSSProperties,
  /** the export's footnote under the pay rail */
  footnote: { fontFamily: "var(--sg-font-web)", fontSize: 12.5, lineHeight: 1.5, color: "var(--sg-grey-500)" } as React.CSSProperties,
  /** the export's member/notice banner: orange-50 on a 1px orange border */
  notice: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "13px 16px",
    background: "var(--sg-orange-50)",
    border: `1px solid ${ORANGE}`,
    borderRadius: 12,
    fontSize: 13.5,
    lineHeight: 1.5,
    color: "var(--sg-ink-900)",
  } as React.CSSProperties,
} as const;

const LOGO = "/_next/static/media/headerLogoNew2.713156d5.svg";
const CHEVRON = "/_mirror/_next/static/media/blueIcon.f4019d62.svg";
const CART_ICON = "/_next/static/media/openCartIcon.f3aa487e.svg";
const PROFILE_ICON = "/_next/static/media/profileIcon.4bf36b73.svg";

/** live's mode row on the site bar; TRANSPORT is the funnel's active mode. */
const MODES: { key: "marketplace" | "transport" | "directory"; label: string; disabled?: boolean }[] = [
  { key: "marketplace", label: "MARKETPLACE" },
  { key: "transport", label: "TRANSPORT" },
  { key: "directory", label: "DIRECTORY" },
];
const MODE_DISABLED = ["FLIGHTS", "STAYS"];

/**
 * S21 · F139 — the F122 site bar, carried onto the funnel (which the build omitted). It is not a mirror
 * subtree here (no captured CSSOM), so it is self-styled with live's tokens exactly like the rest of the
 * funnel. Real wiring: the mountain PILL opens the change-mountain menu (the 30 live resorts, via the same
 * resortHubHref every other header uses); the mode options are the shared hudNavHref links; the cart opens
 * the same tray every add path uses; the profile opens the W6 login popup. `mountainName` is the pill label.
 */
export function FunnelChrome({
  mountainCode,
  mountainName,
}: {
  mountainCode: string;
  mountainName: string;
}) {
  const count = useCartStore((s) => s.items.length);
  const setAuthOpen = useCartStore((s) => s.setAuthOpen);
  const badge = mountainBadgeSrc(mountainCode);

  return (
    <header data-funnel-chrome style={chromeBar}>
      <a aria-label="Home" href="/" style={{ display: "flex", alignItems: "center" }}>
        <img alt="Logo" src={LOGO} style={{ height: 40, width: "auto" }} />
      </a>

      {/* S23 · F142 — the ACTUAL F122 pill: the mountain's own LOGO (badge) + name + a `+`, matching the
          Figma chrome pill (its weather glyph needs a live source and is gated, F73). Clicking it opens the
          full mountain-select modal (F125), NOT a bare dropdown — the founder's ask. */}
      <button
        type="button"
        data-funnel-pill
        data-behavior="nav-toggle"
        aria-label="Change mountain"
        aria-haspopup="dialog"
        // S26 · A3 — the funnel pill was the ONE entry point S25 wired to the R1a selector, via a modal
        // it mounted itself. It now goes through the same site-wide layer as every other entry point, so
        // "which selector opens here" is no longer a per-component decision that can silently diverge.
        onClick={() => openMountainSelect(null)}
        style={pill}
      >
        {badge ? (
          <img alt={mountainName} src={badge} style={{ height: 26, width: "auto" }} />
        ) : (
          <span style={{ fontFamily: "var(--sg-font-secondary)", fontWeight: 700, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--sg-ink-900)" }}>{mountainName}</span>
        )}
        <span aria-hidden style={{ color: ORANGE, fontSize: 16, fontWeight: 700, lineHeight: 1 }}>+</span>
      </button>

      {/* mode options — the SAME hudNavHref links the mirror hub header uses; TRANSPORT is active */}
      {/* FWLR-025-R6 — measured on staging b2375108 at 390 (/cart): the wrapped bar puts this nav beside the icon
          cluster, `flexShrink: 1` squeezes it to the leftover width, and `overflowX: "auto"` with the scrollbar
          hidden CLIPPED "FLIGHTS" / "STAYS" with no affordance. The nav now wraps its own links instead of
          clipping them; desktop is untouched by construction (at 1440 the row has room and never wraps). */}
      <nav aria-label="Modes" style={{ display: "flex", flexWrap: "wrap", gap: 4, marginLeft: 8, minWidth: 0, flexShrink: 1 }}>
        {MODES.map((m) => {
          const href = hudNavHref(mountainCode, m.key);
          const active = m.key === "transport";
          return href ? (
            <a key={m.key} data-funnel-mode={m.key} data-mode-active={active ? "true" : "false"} href={href}
               style={{ ...modeLink, color: active ? ORANGE : "var(--sg-ink-900)" }}>
              {m.label}
            </a>
          ) : (
            <span key={m.key} style={{ ...modeLink, color: GREY }}>{m.label}</span>
          );
        })}
        {MODE_DISABLED.map((l) => (
          <span key={l} className="notAllowed" style={{ ...modeLink, color: "var(--sg-grey-400)", cursor: "not-allowed" }}>{l}</span>
        ))}
      </nav>

      {/* F1 — `marginLeft:"auto"` is what pushed this cluster to x=719 on a 390px viewport: in a
          non-wrapping row it claims all remaining space and then keeps going. It stays (it is what holds
          the icons right on desktop) but the cluster can no longer widen the bar. */}
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, minWidth: 0, flexShrink: 0 }}>
        {/* F141 — NO React onClick here: MarketplaceCartLayer (mounted in FunnelShell) wires every
            button[aria-label="Open cart"] to setOpen(true), exactly as it does on every mirror page. A
            local toggleOpen would fight that listener (setOpen(true) then toggle back to false) and the
            tray would never open — which is precisely the founder's "cart icon not clickable". */}
        <button type="button" data-funnel-cart aria-label="Open cart" style={iconBtn}>
          <img alt="" src={CART_ICON} style={{ width: 32, height: 24, display: "block" }} />
          {count > 0 ? <span data-funnel-cart-count style={cartBadge}>{count}</span> : null}
        </button>
        <button type="button" data-behavior="signin-open" aria-label="Sign in" onClick={() => setAuthOpen(true)} style={iconBtn}>
          <MemberAvatarImg alt="Profile" width={32} height={32} fallback={PROFILE_ICON} style={{ width: 32, height: 32, display: "block" }} />
        </button>
      </div>
    </header>
  );
}

/** Contract §0 — the six steps, in live's order. */
export type StepKey = "transport" | "depart" | "return" | "details" | "marketplace" | "review";

const STEPS: { key: StepKey; label: string; marker: "round" | "square" }[] = [
  { key: "transport", label: "TRANSPORT", marker: "square" },
  { key: "depart", label: "DEPART", marker: "round" },
  { key: "return", label: "RETURN", marker: "round" },
  { key: "details", label: "DETAILS", marker: "round" },
  { key: "marketplace", label: "MARKETPLACE", marker: "square" },
  { key: "review", label: "REVIEW & PAY", marker: "round" },
];

/**
 * Contract §0 + the addendum's §6 correction. The MARKETPLACE *label* links OUT to the mountain's
 * marketplace — that is live's behaviour and it is why the marker is a square — while the funnel's own
 * post-DETAILS step is the in-funnel store at `/booking/store`. BOTH are true; only the label links out.
 */
export default function BookingStepper({
  active,
  mountainCode,
}: {
  active: StepKey;
  mountainCode: string;
}) {
  const activeIdx = STEPS.findIndex((s) => s.key === active);
  return (
    <nav data-booking-stepper={active} aria-label="Booking progress" style={stepperWrap}>
      <ol style={stepperRow}>
        {STEPS.map((s, i) => {
          const isActive = s.key === active;
          const done = i < activeIdx;
          const marketplaceHref = hudNavHref(mountainCode, "marketplace");
          // DoD 10 — the export's funnel rail, step for step: a numbered dot, an uppercase
          // font-secondary label, and a 2.5px underline on the step you are on. Its own six steps are
          // Search · Depart · Return · Details · Booking store · Review & pay — the same six, in the
          // same order, as live's; only the presentation was live's and is now the founder's.
          const label = (
            <span
              data-step={s.key}
              data-step-active={isActive ? "true" : "false"}
              style={{
                fontFamily: "var(--sg-font-secondary)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: isActive ? "var(--sg-ink-900)" : "var(--sg-grey-500)",
                whiteSpace: "nowrap",
              }}
            >
              {s.label}
            </span>
          );
          return (
            <li
              key={s.key}
              style={{
                ...stepItem,
                borderBottom: `2.5px solid ${isActive ? ORANGE : "transparent"}`,
                opacity: done || isActive ? 1 : 0.55,
              }}
            >
              <span
                aria-hidden
                data-step-marker={s.marker}
                style={{
                  ...marker,
                  background: isActive ? ORANGE : done ? "var(--sg-ink-900)" : "#fff",
                  borderColor: isActive ? ORANGE : done ? "var(--sg-ink-900)" : "var(--sg-grey-300)",
                  color: isActive || done ? "#fff" : "var(--sg-grey-500)",
                }}
              >
                {i + 1}
              </span>
              {s.key === "marketplace" && marketplaceHref ? (
                <a data-step-linkout href={marketplaceHref} style={{ textDecoration: "none" }}>
                  {label}
                </a>
              ) : (
                label
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * S21 · F132 → S23 · F140/F121 — the mid-funnel search bar, a REAL re-query form. S21 made the dates/pax/
 * swap live but left From/To as DEAD display divs — the founder walked it, tried to change From/To, and
 * nothing happened (green gates because the harness only tested swap+submit). Now From/To are REAL
 * single-select dropdowns of the mountain's active locations (Techno-Functional "Unique & Active Locations
 * of selected Mountain"; sourced from the captured routes), with SAME-ZONE EXCLUSION (F121: To cannot equal
 * From). The magnitude rebuilds the route slug from the SELECTED From/To codes and re-queries.
 */
export function BookingSearchBar({
  mountainCode,
  routeSlug,
  zones,
  fromLabel,
  toLabel,
  startDate,
  endDate,
  adult,
  child,
  senior,
  student,
  roundTrip: initialRoundTrip,
}: {
  mountainCode: string;
  routeSlug: string;
  zones: { code: string; label: string }[];
  fromLabel: string;
  toLabel: string;
  startDate: string;
  endDate: string;
  adult: number;
  child: number;
  senior: number;
  student: boolean;
  roundTrip: boolean;
}) {
  const [fromSeg0, toSeg0] = (() => {
    const m = routeSlug.match(/^(.+)-to-(.+)$/);
    return m ? [m[1]!, m[2]!] : [routeSlug, ""];
  })();

  const [fromCode, setFromCode] = useState(fromSeg0);
  const [toCode, setToCode] = useState(toSeg0);
  const [open, setOpen] = useState<"from" | "to" | null>(null);
  const [start, setStart] = useState(startDate);
  const [end, setEnd] = useState(endDate);
  const [roundTrip, setRoundTrip] = useState(initialRoundTrip);
  const [pax, setPax] = useState(Math.max(1, adult));

  // label for a code — prefer the zone list, fall back to the route's own source/destination labels
  const labelFor = (code: string, fallback: string) =>
    zones.find((z) => z.code === code)?.label || fallback || code;
  const curFromLabel = labelFor(fromCode, fromLabel);
  const curToLabel = labelFor(toCode, toLabel);

  const swap = () => {
    setFromCode(toCode);
    setToCode(fromCode);
    setOpen(null);
  };
  // ── S29 · F174 — THE TO FIELD WOULD NOT ACCEPT THE SECOND ZONE, AND THIS WAS WHY ────────────────
  // F121's ruling ("both zones on both sides; picking the zone the other field holds SWAPS the pair")
  // was implemented in `TransportSearchLayer` and never propagated to this searchbar. Here `pickFrom`
  // swapped, but `pickTo` `return`ed — and the option was additionally rendered `disabled` — so the
  // asymmetry was total rather than cosmetic:
  //
  //   this mountain serves TWO zones (vancouver, whistler). On the default vancouver→whistler search
  //   the TO menu therefore held exactly two rows: `vancouver`, DISABLED because it equals fromCode,
  //   and `whistler`, already selected. Zero usable choices. The founder opened TO, found nothing he
  //   could pick, and reported the field as broken — correctly. FROM worked because `pickFrom` swaps.
  //
  // Both handlers are now the SAME rule, mirrored, which is what F121 actually ruled: a pick that
  // equals the other side reverses the trip, so `from === to` is unreachable without refusing input.
  const pickFrom = (code: string) => {
    if (code === toCode) setToCode(fromCode); // reverse the trip rather than refuse the pick (F121)
    setFromCode(code);
    setOpen(null);
  };
  const pickTo = (code: string) => {
    if (code === fromCode) setFromCode(toCode); // the mirror of pickFrom — this is F174's fix
    setToCode(code);
    setOpen(null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const slug = toCode ? `${fromCode}-to-${toCode}` : fromCode;
    const p = new URLSearchParams();
    // ── S36 · card 3 · F260 — ROUND TRIP MUST NOT SILENTLY BECOME A ONE-WAY ────────────────────────
    // Measured on the running build 2026-07-29: arrive one-way, click Roundtrip (pill goes orange, the
    // Return field enables EMPTY), press Search — and the submitted URL carried no `endDate`, so the
    // funnel re-rendered as One Way. `roundTrip` is derived from `endDate` end to end (deliberately ONE
    // source of truth), so an empty return date is not a round trip; the old `&& end` quietly discarded
    // the intent instead of asking for the missing fact. The Return input now carries `required` when
    // Roundtrip is selected, so the browser blocks the submit and focuses the field. This guard is the
    // programmatic half of the same rule — it can only ever be reached if that input is bypassed.
    if (roundTrip && !end) return;
    if (roundTrip) p.set("endDate", end);
    p.set("startDate", start);
    p.set("noStore", "false");
    p.set("adult", String(pax));
    p.set("child", String(child));
    p.set("senior", String(senior));
    p.set("student", String(student));
    window.location.href = `/mountain-${mountainCode}/booking/schedule/depart/${slug}?${p.toString()}`;
  };

  // a From/To select: a button that opens a small menu of the mountain's zones.
  const zoneSelect = (which: "from" | "to", label: string, curCode: string, curLabel: string) => (
    <div data-searchbar-field={which} style={{ ...sbField, position: "relative" }}>
      <div style={sbLabel}>{which === "from" ? "From" : "To"}</div>
      <button
        type="button"
        data-searchbar-open={which}
        aria-haspopup="listbox"
        aria-expanded={open === which}
        onClick={() => setOpen((o) => (o === which ? null : which))}
        style={{ ...sbValue, display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", border: "none", background: "transparent", cursor: "pointer", padding: 0, font: "inherit" }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{curLabel || "—"}</span>
        <span aria-hidden style={{ color: GREY, fontSize: 11, marginLeft: 6 }}>▾</span>
      </button>
      {open === which ? (
        <div data-searchbar-menu={which} role="listbox" style={sbMenu}>
          {zones.map((z) => {
            // S29 · F174 — the `excluded`/`disabled` treatment is GONE, not relaxed. It greyed out and
            // un-clicked the only row in the TO menu that was not already selected, which is what made
            // the field inert. Under F121 the counterpart zone is a LEGITIMATE pick on both sides — it
            // means "go the other way" — so it is offered, and `pickFrom`/`pickTo` reverse the pair.
            // `data-excluded` is kept as an attribute and pinned to "false" so the funnel gate can
            // assert that nothing in either menu is excluded any more; deleting the hook outright would
            // have made that assertion impossible to write.
            const selected = z.code === curCode;
            return (
              <button
                key={z.code}
                type="button"
                role="option"
                aria-selected={selected}
                data-searchbar-option={z.code}
                data-excluded="false"
                onClick={() => (which === "from" ? pickFrom(z.code) : pickTo(z.code))}
                style={{ ...sbMenuItem, color: selected ? ORANGE : "var(--sg-ink-900)", cursor: "pointer", fontWeight: selected ? 700 : 500 }}
              >
                {z.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );

  return (
    <form data-booking-searchbar onSubmit={submit} style={sbWrap}>
      <div style={sbTripType}>
        <button type="button" data-trip-type="one-way" onClick={() => setRoundTrip(false)}
          style={{ ...sbPill, color: roundTrip ? GREY : ORANGE, borderColor: roundTrip ? "transparent" : ORANGE }}>
          One Way
        </button>
        <button type="button" data-trip-type="roundtrip" onClick={() => setRoundTrip(true)}
          style={{ ...sbPill, color: roundTrip ? ORANGE : GREY, borderColor: roundTrip ? ORANGE : "transparent" }}>
          Roundtrip
        </button>
      </div>

      {zoneSelect("from", "From", fromCode, curFromLabel)}
      <button type="button" data-searchbar-swap aria-label="Swap from and to" onClick={swap}
        style={{ border: `1px solid ${RULE}`, background: "#fff", borderRadius: "50%", width: 28, height: 28, cursor: "pointer", color: GREY, fontSize: 15, flex: "0 0 28px" }}>
        ⇄
      </button>
      {zoneSelect("to", "To", toCode, curToLabel)}

      <label data-searchbar-field="depart" style={sbField}>
        <div style={sbLabel}>Depart</div>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={sbInput} />
      </label>
      <label data-searchbar-field="return" style={{ ...sbField, opacity: roundTrip ? 1 : 0.5 }}>
        <div style={sbLabel}>Return</div>
        {/* F260 — `required` only while Roundtrip is selected: a one-way has no return date to give. */}
        <input type="date" data-searchbar-return required={roundTrip} value={roundTrip ? end : ""} disabled={!roundTrip} min={start} onChange={(e) => setEnd(e.target.value)} style={sbInput} />
      </label>

      <div data-searchbar-field="pax" style={{ ...sbField, minWidth: 92, flex: "0 0 92px" }}>
        <div style={sbLabel}>Passengers</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button type="button" data-pax-dec aria-label="Fewer passengers" onClick={() => setPax((n) => Math.max(1, n - 1))} style={stepBtn}>−</button>
          <span data-pax-value style={sbValue}>{pax}</span>
          <button type="button" data-pax-inc aria-label="More passengers" onClick={() => setPax((n) => Math.min(10, n + 1))} style={stepBtn}>+</button>
        </div>
      </div>

      <button type="submit" data-searchbar-submit aria-label="Search" style={sbSubmit}>⌕</button>
    </form>
  );
}

// F1 (FOUNDER-WALK-SITE-DEFECTS-005) — THE SECOND HALF OF THE 390px OVERFLOW, found only by measuring
// again after the first fix rather than declaring victory. With BookingResults' mode row wrapped, the
// document still ran to 719px on a 390px viewport, and the shallowest offenders were this header's own
// children: the modes <nav> at 424px wide inside a 374px header, and the right-hand icon cluster ending
// at x=719. A flex row whose items cannot shrink pushes past its parent, and because nothing clips it the
// overflow reaches the document and every funnel screen scrolls sideways.
// `flexWrap: "wrap"` lets the row fold on a phone; `maxWidth: "100%"` and the `minWidth: 0` on the two
// flexible children stop an unshrinkable item from widening the bar in the first place. Desktop is
// untouched by construction: at 1440 the row has room, so it never wraps and the measured geometry is
// identical (verified: scrollWidth 1440, zero overflowing elements, mode row right edge unchanged).
const chromeBar: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  maxWidth: "100%",
  gap: 12,
  padding: "12px 22px",
  borderBottom: `1px solid ${RULE}`,
  background: "#fff",
  fontFamily: "var(--sg-font-web)",
};
const pill: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${RULE}`,
  borderRadius: 999,
  padding: "6px 14px",
  background: "#fff",
  cursor: "pointer",
};
// the export's HUD nav: font-secondary 11px/700 uppercase, .08em — not live's 12px Roboto row.
const modeLink: React.CSSProperties = { padding: "6px 10px", fontFamily: "var(--sg-font-secondary)", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none", background: "none", border: "none", cursor: "pointer" };
const iconBtn: React.CSSProperties = { position: "relative", border: "none", background: "none", cursor: "pointer", padding: 4, lineHeight: 0 };
const cartBadge: React.CSSProperties = { position: "absolute", top: 0, right: 0, background: ORANGE, color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: "50%", minWidth: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" };

const stepperWrap: React.CSSProperties = { borderBottom: `1px solid ${RULE}`, padding: "12px 22px 0", background: "#fff", fontFamily: "var(--sg-font-web)" };
const stepperRow: React.CSSProperties = {
  listStyle: "none",
  margin: "0 auto",
  padding: 0,
  maxWidth: 1140,
  display: "flex",
  alignItems: "center",
  overflowX: "auto",
  gap: 4,
};
const stepItem: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, flex: "none", padding: "4px 14px 8px 0" };
/** the export's step dot: 20px, 1px border, radius 999, font-secondary 10/700 */
const marker: React.CSSProperties = {
  width: 20,
  height: 20,
  flex: "none",
  display: "grid",
  placeItems: "center",
  border: "1px solid",
  borderRadius: 999,
  fontFamily: "var(--sg-font-secondary)",
  fontSize: 10,
  fontWeight: 700,
};
const sbWrap: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "14px 16px",
  maxWidth: 1180,
  margin: "0 auto",
  flexWrap: "wrap",
  fontFamily: "var(--sg-font-web)",
};
const sbTripType: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const sbPill: React.CSSProperties = {
  border: "1px solid",
  borderRadius: 999,
  padding: "2px 12px",
  fontSize: 11,
  fontWeight: 700,
  textAlign: "center",
  background: "#fff",
  cursor: "pointer",
};
const sbField: React.CSSProperties = {
  border: `1px solid ${RULE}`,
  borderRadius: 10,
  padding: "6px 12px",
  minWidth: 120,
  flex: 1,
};
const sbLabel: React.CSSProperties = { fontFamily: "var(--sg-font-secondary)", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--sg-grey-500)" };
const sbValue: React.CSSProperties = { fontSize: 14, fontWeight: 600, color: "var(--sg-ink-900)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const sbInput: React.CSSProperties = { border: "none", outline: "none", fontSize: 14, fontWeight: 600, color: "var(--sg-ink-900)", width: "100%", background: "transparent", fontFamily: "inherit", padding: 0 };
const sbMenu: React.CSSProperties = { position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 1200, minWidth: "100%", background: "#fff", border: `1px solid ${RULE}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,.18)", padding: "6px 0", maxHeight: 240, overflowY: "auto" };
const sbMenuItem: React.CSSProperties = { display: "block", width: "100%", textAlign: "left", padding: "8px 14px", border: "none", background: "transparent", fontSize: 14, whiteSpace: "nowrap", fontFamily: "inherit" };
const stepBtn: React.CSSProperties = { border: `1px solid ${RULE}`, background: "#fff", borderRadius: 4, width: 22, height: 22, cursor: "pointer", color: GREY, fontSize: 14, lineHeight: 1 };
const sbSubmit: React.CSSProperties = { border: "none", background: "var(--sg-glacier-300)", color: "#fff", borderRadius: 10, width: 46, height: 46, fontSize: 20, cursor: "pointer", flex: "0 0 46px" };
